/**
 * Centralized Analytics Event Names
 *
 * Naming Convention: skillzen_{portal}_{context}_{action}
 * - Portal: admin | learner
 * - Context: page/widget/component name
 * - Action: what happened
 */

// ============================================
// ADMIN DASHBOARD EVENTS
// ============================================

export const ADMIN_DASHBOARD_EVENTS = {
  // Page Level
  PAGE_VIEW: 'skillzen_admin_dashboard_page_view',
  TIME_ON_PAGE: 'skillzen_admin_dashboard_time_on_page',
  SCROLL_DEPTH: 'skillzen_admin_dashboard_scroll_depth',

  // Filters
  FILTER_TIME_RANGE: 'skillzen_admin_dashboard_filter_time_range',
  FILTER_CUSTOM_DATE: 'skillzen_admin_dashboard_filter_custom_date',

  // Stats Cards Widget
  WIDGET_STATS_CARDS_VIEW: 'skillzen_admin_dashboard_widget_stats_cards_view',
  WIDGET_STATS_CARDS_CLICK: 'skillzen_admin_dashboard_widget_stats_cards_click',

  // Department Performance Widget
  WIDGET_DEPT_PERFORMANCE_VIEW: 'skillzen_admin_dashboard_widget_dept_performance_view',
  WIDGET_DEPT_PERFORMANCE_CLICK: 'skillzen_admin_dashboard_widget_dept_performance_click',

  // Skills Radar Widget
  WIDGET_SKILLS_RADAR_VIEW: 'skillzen_admin_dashboard_widget_skills_radar_view',
  WIDGET_SKILLS_RADAR_CLICK: 'skillzen_admin_dashboard_widget_skills_radar_click',

  // Activity Heatmap Widget
  WIDGET_ACTIVITY_HEATMAP_VIEW: 'skillzen_admin_dashboard_widget_activity_heatmap_view',
  WIDGET_ACTIVITY_HEATMAP_CLICK: 'skillzen_admin_dashboard_widget_activity_heatmap_click',

  // Activity Timeline Widget
  WIDGET_ACTIVITY_TIMELINE_VIEW: 'skillzen_admin_dashboard_widget_activity_timeline_view',
  WIDGET_ACTIVITY_TIMELINE_CLICK: 'skillzen_admin_dashboard_widget_activity_timeline_click',

  // Navigation
  NAVIGATION_VIEW_ALL_ACTIVITY: 'skillzen_admin_dashboard_navigation_view_all_activity',

  // Errors
  ERROR_STATS_LOAD_FAILED: 'skillzen_admin_dashboard_error_stats_load_failed',
} as const;

export type AdminDashboardEventName = typeof ADMIN_DASHBOARD_EVENTS[keyof typeof ADMIN_DASHBOARD_EVENTS];

// ============================================
// LEARNER DASHBOARD EVENTS
// ============================================

export const LEARNER_DASHBOARD_EVENTS = {
  // Page Level
  PAGE_VIEW: 'skillzen_learner_dashboard_page_view',
  TIME_ON_PAGE: 'skillzen_learner_dashboard_time_on_page',
  SCROLL_DEPTH: 'skillzen_learner_dashboard_scroll_depth',

  // Header Quick Stats Widget
  WIDGET_QUICK_STATS_VIEW: 'skillzen_learner_dashboard_widget_quick_stats_view',

  // Upcoming Deadlines Widget
  WIDGET_DEADLINES_VIEW: 'skillzen_learner_dashboard_widget_deadlines_view',
  WIDGET_DEADLINES_CLICK: 'skillzen_learner_dashboard_widget_deadlines_click',

  // Continue Learning Widget
  WIDGET_CONTINUE_LEARNING_VIEW: 'skillzen_learner_dashboard_widget_continue_learning_view',
  WIDGET_CONTINUE_LEARNING_CLICK: 'skillzen_learner_dashboard_widget_continue_learning_click',

  // Streak Widget
  WIDGET_STREAK_VIEW: 'skillzen_learner_dashboard_widget_streak_view',

  // Course Actions
  COURSE_RESUME_CLICK: 'skillzen_learner_dashboard_course_resume_click',
  COURSE_EXPLORE_CLICK: 'skillzen_learner_dashboard_course_explore_click',

  // Navigation
  NAVIGATION_SCHEDULE: 'skillzen_learner_dashboard_navigation_schedule',
  NAVIGATION_VIEW_ALL_COURSES: 'skillzen_learner_dashboard_navigation_view_all_courses',

  // Errors
  ERROR_STATS_LOAD_FAILED: 'skillzen_learner_dashboard_error_stats_load_failed',
  ERROR_COURSES_LOAD_FAILED: 'skillzen_learner_dashboard_error_courses_load_failed',
  ERROR_STREAK_LOAD_FAILED: 'skillzen_learner_dashboard_error_streak_load_failed',
} as const;

export type LearnerDashboardEventName = typeof LEARNER_DASHBOARD_EVENTS[keyof typeof LEARNER_DASHBOARD_EVENTS];

// ============================================
// LEARNER SCHEDULE EVENTS
// ============================================

export const LEARNER_SCHEDULE_EVENTS = {
  // Page Level
  PAGE_VIEW: 'skillzen_learner_schedule_page_view',
  TIME_ON_PAGE: 'skillzen_learner_schedule_time_on_page',
  SCROLL_DEPTH: 'skillzen_learner_schedule_scroll_depth',

  // Header Stats Widget
  WIDGET_STATS_VIEW: 'skillzen_learner_schedule_widget_stats_view',

  // Calendar Widget
  WIDGET_CALENDAR_VIEW: 'skillzen_learner_schedule_widget_calendar_view',
  CALENDAR_VIEW_CHANGE: 'skillzen_learner_schedule_calendar_view_change',
  CALENDAR_NAVIGATE_MONTH: 'skillzen_learner_schedule_calendar_navigate_month',
  CALENDAR_NAVIGATE_WEEK: 'skillzen_learner_schedule_calendar_navigate_week',
  CALENDAR_NAVIGATE_DAY: 'skillzen_learner_schedule_calendar_navigate_day',
  CALENDAR_TODAY_CLICK: 'skillzen_learner_schedule_calendar_today_click',
  CALENDAR_DAY_CLICK: 'skillzen_learner_schedule_calendar_day_click',

  // Sidebar Widgets
  WIDGET_TODAY_SCHEDULE_VIEW: 'skillzen_learner_schedule_widget_today_schedule_view',
  WIDGET_UPCOMING_DEADLINES_VIEW: 'skillzen_learner_schedule_widget_upcoming_deadlines_view',
  WIDGET_OVERDUE_VIEW: 'skillzen_learner_schedule_widget_overdue_view',

  // Event Interactions
  EVENT_CLICK: 'skillzen_learner_schedule_event_click',
  EVENT_MODAL_OPEN: 'skillzen_learner_schedule_event_modal_open',
  EVENT_MODAL_CLOSE: 'skillzen_learner_schedule_event_modal_close',
  EVENT_GO_TO_COURSE: 'skillzen_learner_schedule_event_go_to_course',

  // Errors
  ERROR_CALENDAR_LOAD_FAILED: 'skillzen_learner_schedule_error_calendar_load_failed',
  ERROR_STATS_LOAD_FAILED: 'skillzen_learner_schedule_error_stats_load_failed',
} as const;

export type LearnerScheduleEventName = typeof LEARNER_SCHEDULE_EVENTS[keyof typeof LEARNER_SCHEDULE_EVENTS];
